import { graphqlFetch } from '@api/graphql/gql-fetch';
import { buildVariablesForReviewForGallery } from '@api/reviews/getReviewsForGallery/build-variables';
import { fetchReviewsForGallery } from '@api/reviews/getReviewsForGallery/fetch';
import { buildVariablesForGetReviewsForStorefront } from '@api/reviews/getReviewsForStorefront/build-variables';
import { fetchReviewsForStorefront } from '@api/reviews/getReviewsForStorefront/fetch';
import { buildVariablesForReviewForVendors } from '@api/reviews/reviewsForVendor/build-variables';
import {
	fetchHasCurrentUserReviewedVendor,
	fetchReviewsForVendors,
} from '@api/reviews/reviewsForVendor/fetch';
import { googleReviewsUrl } from '@settings';
import type {
	GoogleReviewsResponse,
	ReviewsResponse,
	ReviewsResponseData,
} from 'types/api/reviews';
import googleReviewsDataQuery from '../graphql/googleReviews';
import {
	REVIEWS_FOR_PAGE_1,
	REVIEWS_PER_PAGE,
	pageAfterFirst,
} from './constants';
import { postAuthedStorefrontReview as fetchPostAuthedStorefrontReview } from './postAuthedStorefrontReview/fetch';

export { pageAfterFirst };
export type ReviewsSort =
	| 'selectiontype:asc|createddate:desc'
	| 'createddate:desc'
	| 'createddate:asc'
	| 'rating:desc'
	| 'rating:asc';

import { fetchMemberReviews } from '@api/reviews/getReviewsForMember/fetch';
import { mapGetReviewsForMember } from '@api/reviews/getReviewsForMember/mappers';
import { buildVariablesForUpdateReview } from '@api/reviews/updateReview/build-variables';
import { updateReviewFetch } from '@api/reviews/updateReview/fetch';
import type { ReviewFormSubmission } from '@components/reviews/components/reviews_modal_ver_two/types';
import { buildVariablesForPostAuthedStorefrontReview } from './postAuthedStorefrontReview/build-variables';

export const reviewsForVendor = async (
	vendorId: string,
	sort: ReviewsSort,
	page: number,
	gallerySize = 10,
): Promise<ReviewsResponse> => {
	const variables = await buildVariablesForReviewForVendors(vendorId, {
		size: page === 1 ? REVIEWS_FOR_PAGE_1 : REVIEWS_PER_PAGE,
		sort,
		page,
		gallerySize,
	});

	return fetchReviewsForVendors(vendorId, variables);
};

export const hasCurrentUserReviewedVendor = async (
	vendorId: string,
	options?: { membershipSessionToken?: string | null },
): Promise<boolean> => {
	return fetchHasCurrentUserReviewedVendor(vendorId, options);
};

export const googleReviewsForVendor = async (
	vendorId: string,
): Promise<GoogleReviewsResponse> => {
	const data = {
		url: googleReviewsUrl,
		query: googleReviewsDataQuery,
		variables: {
			storefrontId: vendorId,
		},
		headers: new Headers({ 'x-tenant-id': 'tk-us' }),
	};

	const googleReviewsResponse = await graphqlFetch(data);

	return googleReviewsResponse?.data?.getGooglePlace;
};

export interface GetReviewsForStorefrontQueryParams {
	content: string | undefined;
	page: number;
	stars: number[] | undefined;
	sort: ReviewsSort;
	hasPhotos?: boolean;
}

export async function getReviewsForMember(memberId: string) {
	const reviews = await fetchMemberReviews(memberId);
	const mappedReviews = mapGetReviewsForMember(reviews);
	return {
		data: mappedReviews,
		totalCount: mappedReviews.length,
	};
}

export interface GetReviewsForStorefrontParams {
	vendorId: string;
	content?: string;
	page: number;
	stars?: number[];
	sort: ReviewsSort;
	hasPhotos?: boolean;
}

export const getReviewsForStorefront = async (
	{
		vendorId,
		sort,
		page,
		stars,
		content,
		hasPhotos,
	}: GetReviewsForStorefrontParams,
	signal?: AbortSignal,
): Promise<ReviewsResponseData> => {
	const variables = await buildVariablesForGetReviewsForStorefront(vendorId, {
		size: page === 1 ? REVIEWS_FOR_PAGE_1 : REVIEWS_PER_PAGE,
		sort,
		page,
		stars,
		content,
		hasPhotos,
	});

	return fetchReviewsForStorefront(vendorId, variables, signal);
};

export async function getReviewsForGallery(vendorId: string, page: number) {
	const size = page === 1 ? REVIEWS_FOR_PAGE_1 : REVIEWS_PER_PAGE;

	const variables = await buildVariablesForReviewForGallery(vendorId, {
		size,
		page,
	});

	return fetchReviewsForGallery(variables);
}

export async function postAuthedStorefrontReview(
	vendorId: string,
	memberId: string,
	data: ReviewFormSubmission,
	token: string,
) {
	const variables = await buildVariablesForPostAuthedStorefrontReview(
		vendorId,
		memberId,
		data,
	);

	return fetchPostAuthedStorefrontReview(token, variables);
}

export async function updateReview(
	data: ReviewFormSubmission,
	token: string,
	reviewId: string,
) {
	const variables = await buildVariablesForUpdateReview({
		data,
		reviewId,
	});
	return updateReviewFetch(variables, token);
}
